import React, { useState, useEffect } from 'react';
import { listInstances, createSnapshot } from '../services/rdsService';

const TakeSnapshotModal = ({ onClose, onSnapshotCreated, awsCredentials }) => {
  const [instances, setInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState('');
  const [snapshotName, setSnapshotName] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingInstances, setLoadingInstances] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDBInstances();
  }, []);

  useEffect(() => {
    if (selectedInstance) {
      // Auto-generate snapshot name when instance is selected
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      setSnapshotName(`${selectedInstance}-manual-${timestamp}`);
    }
  }, [selectedInstance]);

  const loadDBInstances = async () => {
    try {
      setLoadingInstances(true);
      console.log('Loading DB instances for snapshot creation...');
      const data = await listInstances(awsCredentials);
      
      // Filter only available instances
      const availableInstances = data.filter(instance => 
        instance.DBInstanceStatus === 'available'
      );
      
      setInstances(availableInstances);
      console.log(`Found ${availableInstances.length} available DB instances`);
      setError(null);
    } catch (err) {
      console.error('Error loading instances:', err);
      setError('Failed to load DB instances: ' + err.message);
    } finally {
      setLoadingInstances(false);
    }
  };

  const handleCreateSnapshot = async () => {
    if (!selectedInstance || !snapshotName.trim()) {
      setError('Please select a DB instance and enter a snapshot name');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await createSnapshot(selectedInstance, snapshotName.trim(), awsCredentials);
      
      // Notify parent component that snapshot was created
      if (onSnapshotCreated) {
        onSnapshotCreated();
      }
      
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="aws-modal-overlay">
      <div className="aws-modal">
        <div className="aws-modal-header">
          <h2 className="aws-modal-title">Take snapshot</h2>
        </div>
        
        <div className="aws-modal-body">
          {error && (
            <div className="aws-alert aws-alert-error aws-mb-3">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="aws-alert aws-alert-info aws-mb-3">
            <strong>Create a manual snapshot</strong><br />
            Manual snapshots are retained until you delete them and are useful for creating backups before making changes to your database.
          </div>

          <div className="aws-form-group">
            <label className="aws-form-label">Source DB instance *</label>
            <select
              className="aws-form-select"
              value={selectedInstance}
              onChange={(e) => setSelectedInstance(e.target.value)}
              disabled={loadingInstances || loading}
            >
              <option value="">
                {loadingInstances ? 'Loading instances...' : 'Select a DB instance'}
              </option>
              {instances.map(instance => (
                <option key={instance.DBInstanceIdentifier} value={instance.DBInstanceIdentifier}>
                  {instance.DBInstanceIdentifier} ({instance.Engine} {instance.EngineVersion}) - {instance.DBInstanceStatus}
                </option>
              ))}
            </select>
            {loadingInstances && (
              <div style={{ fontSize: '12px', color: '#687078', marginTop: '4px' }}>
                Loading available DB instances...
              </div>
            )}
          </div>

          <div className="aws-form-group">
            <label className="aws-form-label">Snapshot identifier *</label>
            <input
              type="text"
              className="aws-form-input"
              value={snapshotName}
              onChange={(e) => setSnapshotName(e.target.value)}
              placeholder="Enter snapshot identifier"
              disabled={loading}
            />
            <div style={{ fontSize: '12px', color: '#687078', marginTop: '4px' }}>
              Must be 1-255 characters. Can contain letters, numbers, and hyphens.
            </div>
          </div>

          {selectedInstance && (
            <div className="aws-form-group">
              <label className="aws-form-label">Source instance details</label>
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#f8f9fa', 
                border: '1px solid #d5dbdb', 
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                {(() => {
                  const instance = instances.find(i => i.DBInstanceIdentifier === selectedInstance);
                  return instance ? (
                    <div>
                      <strong>{instance.DBInstanceIdentifier}</strong><br />
                      Engine: {instance.Engine} {instance.EngineVersion}<br />
                      Class: {instance.DBInstanceClass}<br />
                      Status: {instance.DBInstanceStatus}<br />
                      {instance.Endpoint && (
                        <>Endpoint: {instance.Endpoint.Address}:{instance.Endpoint.Port}</>
                      )}
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
          )}
        </div>

        <div className="aws-modal-footer">
          <button
            className="aws-button aws-button-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="aws-button"
            onClick={handleCreateSnapshot}
            disabled={loading || !selectedInstance || !snapshotName.trim() || loadingInstances}
          >
            {loading ? 'Creating snapshot...' : 'Take snapshot'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TakeSnapshotModal;